// PaymentConfirmModal.tsx

import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { ToastContainer } from "react-toastify";

import LoadingScreen from "../../../../../../shared/components/LoadingScreen";
import Print from "../../../../../../print-configuration/PrintConfigration";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper";

import CommonButtons from "src/shared/components/CommonButtons";

import { CustomerData, Order } from "src/shared/services/interfacev2";

import { useInvoiceEmail } from "src/business-types/laundry/modules/home/Done/hooks/useInvoiceEmail";
import { useCustomer } from "src/business-types/laundry/modules/home/Done/hooks/useCustomer";

import { notifyError, notifySuccess } from "src/assets/toastService";

interface Props {
  getorder: Order | undefined;
  confirm: boolean;
  Customer: CustomerData | undefined;
  printData: any;
  filteredCustomerId: string | number;
  Dones: string;
  loading: boolean;
  setConfirm: (val: boolean) => void;
  onOrderComplete: () => void;
}

const PaymentConfirmModal: React.FC<Props> = ({ getorder, confirm, setConfirm, Customer, printData, filteredCustomerId, Dones, loading, onOrderComplete }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [email, setEmail] = useState("");

  const [emailError, setEmailError] = useState("");

  const [isSending, setIsSending] = useState(false);

  // FIXED EMAIL STATES
  const [primaryEmail, setPrimaryEmail] = useState("");

  const [hasEmail, setHasEmail] = useState(false);

  const { sendInvoiceEmail } = useInvoiceEmail();

  const customerId = getorder?.orders?.[0]?.customerId;

  const { updateEmail } = useCustomer(customerId);

  // EMAIL VALIDATION
  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  // GET PRIMARY EMAIL
  const getPrimaryEmail = () => {

    // FIRST PRIORITY -> Customer prop
    if (Customer?.emails?.length) {
      const primary = Customer.emails.find((e) => e.isPrimary)?.email || Customer.emails[0]?.email;

      if (primary?.trim()) {
        return primary;
      }
    }

    // SECOND PRIORITY -> getorder.customerDetails
    if (getorder?.customerDetails?.emails?.length) {
      const primary = getorder.customerDetails.emails.find((e) => e.isPrimary)?.email || getorder.customerDetails.emails[0]?.email;

      if (primary?.trim()) {
        return primary;
      }
    }

    // THIRD PRIORITY -> direct order email
    const orderData = getorder?.orders?.[0] as any;

    if (orderData?.CustomerEmail?.trim()) {
      return orderData.CustomerEmail;
    }

    if (orderData?.email?.trim()) {
      return orderData.email;
    }

    return "";
  };

  // DETECT EMAIL
  useEffect(() => {
    const detectedEmail = getPrimaryEmail();

    const validEmail = Boolean(detectedEmail && typeof detectedEmail === "string" && detectedEmail.trim());

    setPrimaryEmail(detectedEmail || "");

    setHasEmail(validEmail);

    // autofill input
    if (validEmail) {
      setEmail(detectedEmail);
    }
  }, [Customer, getorder]);

  // SEND MAIL
  const handleSendMail = async () => {

    // IF EMAIL EXISTS -> SEND DIRECTLY
    if (hasEmail && primaryEmail) {
      setIsSending(true);

      try {
        await sendInvoiceEmail(primaryEmail, getorder);

        notifySuccess("Invoice sent successfully!");

        setTimeout(() => {
          handleClose();
        }, 1500);
      } catch (error) {
        console.error(error);

        notifyError("Failed to send invoice email");
      } finally {
        setIsSending(false);
      }

      return;
    }

    // ONLY SHOW POPUP IF NO EMAIL
    setShowEmailModal(true);
  };

  // UPDATE EMAIL
  const handleUpdateEmail = async () => {
    if (!email.trim()) {
      setEmailError("Email is required");

      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address");

      return;
    }

    try {
      if (!customerId) {
        notifyError("Customer ID not found");

        return;
      }

      setIsSending(true);

      const payload = {
        emails: [
          {
            email: email.trim(),
            isPrimary: true,
          },
        ],
      };

      await updateEmail(customerId, payload);

      notifySuccess("Email updated successfully!");

      // SEND MAIL AFTER UPDATE
      await sendInvoiceEmail(email.trim(), getorder);

      notifySuccess("Invoice sent successfully!");

      setShowEmailModal(false);

      setEmailError("");

      handleClose();
    } catch (error: any) {
      console.error(error);

      const backendMsg = error?.response?.data?.message;

      if (backendMsg === "Email already exists") {
        setEmailError("Email already exists.");

        return;
      }

      notifyError(backendMsg || "Failed to update email.");

      setEmailError("Failed to update email. Try again.");
    } finally {
      setIsSending(false);
    }
  };

  // CLOSE
  const handleClose = () => {
    setConfirm(false);

    onOrderComplete();
  };

  return (
    <>
      {confirm && (
        <ModalWrapper title="Payment Completed" onClose={handleClose} showCloseButton={true} width="max-w-lg">
          <div className="flex flex-col items-center text-center space-y-4">
            <img src={Dones} alt="success" className="w-48 h-48" />

            <p className="text-lg font-medium text-gray-700">Your payment has been completed successfully</p>

            <div className="mt-6 gap-3 flex justify-center">
              <CommonButtons label={isSending ? "Sending..." : "Send Mail"} onClick={handleSendMail} disabled={isSending} />

              <CommonButtons label="Send Message" onClick={() => ""} />

              <div onClick={handleClose}>
                <Print
                  PrintData={{
                    ...printData,
                    Paid: printData.Paid ?? 0,
                    clientId: filteredCustomerId || "0",
                  }}
                  BtnName={"Done"}
                  username=""
                />
              </div>
            </div>
          </div>

          {/* ONLY SHOW IF NO EMAIL */}
          {showEmailModal && (
            <ModalWrapper
              title="Please enter the Email address"
              onClose={() => {
                setShowEmailModal(false);

                setEmailError("");
              }}
              showCloseButton={true}
              width="max-w-lg"
            >
              <div className="space-y-6">
                <TextField
                  className="w-full"
                  label="Email ID"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);

                    setEmailError("");
                  }}
                  error={!!emailError}
                  helperText={emailError || "Please enter your email in the format 'id@example.com'"}
                  variant="outlined"
                />

                <div className="flex justify-end gap-4">
                  <CommonButtons
                    className="w-36"
                    variant="outlineDanger"
                    label="Cancel"
                    onClick={() => {
                      setShowEmailModal(false);

                      setEmailError("");
                    }}
                  />

                  <CommonButtons className="w-36" label={isSending ? "Updating..." : "Update"} onClick={handleUpdateEmail} disabled={!email || !!emailError || isSending} />
                </div>
              </div>
            </ModalWrapper>
          )}

          {loading && <LoadingScreen />}

          <ToastContainer />
        </ModalWrapper>
      )}
    </>
  );
};

export default PaymentConfirmModal;
